import React, { useState, useEffect, useMemo } from 'react';
import { Spinner, Alert, Button, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import axios from '../api/axios';

const MediaWithSignedUrl = React.memo(
  ({
    mediaUrl,
    alt = '',
    className = '',
    style = {},
    onClick,
    isThumbnail = false,
    mediaType = null,
  }) => {
    const [signedUrl, setSignedUrl] = useState(null);
    const [loadingState, setLoadingState] = useState(true);

    const isVideo = mediaType?.startsWith('video/') || /\.(mp4|webm|ogg)$/i.test(mediaUrl);

    useEffect(() => {
      let mounted = true;

      const load = async () => {
        if (
          mediaUrl.startsWith('blob:') ||
          mediaUrl.startsWith('data:') ||
          mediaUrl.includes('?') ||
          mediaUrl.includes('Expires=') ||
          mediaUrl.includes('Signature=') ||
          mediaUrl.includes('X-Amz-')
        ) {
          if (mounted) {
            setSignedUrl(mediaUrl);
            setLoadingState(false);
          }
          return;
        }

        try {
          const res = await axios.post('v2/inventory/consume/get-signed-urls', {
            urls: [mediaUrl]
          });
          if (res.data.success && res.data.signed_urls?.length) {
            if (mounted) setSignedUrl(res.data.signed_urls[0].signed_url || mediaUrl);
          } else {
            if (mounted) setSignedUrl(mediaUrl);
          }
        } catch {
          if (mounted) setSignedUrl(mediaUrl);
        } finally {
          if (mounted) setLoadingState(false);
        }
      };

      load();

      return () => {
        mounted = false;
      };
    }, [mediaUrl]);

    if (loadingState) {
      return (
        <div className={className} style={style}>
          <Spinner size="sm" />
        </div>
      );
    }

    const imgStyle = isThumbnail ? { width: '64px', height: '64px', objectFit: 'cover' } : {};
    const videoStyle = isThumbnail ? { width: '64px', height: '64px', objectFit: 'cover' } : {};

    if (isVideo) {
      return (
        <div
          className="position-relative"
          style={{ cursor: isThumbnail ? 'pointer' : 'default', ...style }}
          onClick={onClick}
        >
          <video
            src={signedUrl}
            controls={!isThumbnail}
            preload="metadata"
            className={className}
            style={{ ...videoStyle }}
            loading="lazy"
          />
          {isThumbnail && (
            <div
              className="position-absolute top-50 start-50 translate-middle bg-dark bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: '32px', height: '32px' }}
            >
              <FeatherIcon icon="play" size={14} className="text-white ms-1" />
            </div>
          )}
        </div>
      );
    }

    return (
      <img
        src={signedUrl}
        alt={alt}
        className={className}
        style={{ ...style, ...imgStyle, cursor: isThumbnail ? 'pointer' : 'default' }}
        onClick={onClick}
        loading="lazy"
        decoding="async"
        width={isThumbnail ? 64 : undefined}
        height={isThumbnail ? 64 : undefined}
      />
    );
  }
);

export const TicketHistoryTimeline = ({ ticketId }) => {
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagePreviewModal, setImagePreviewModal] = useState(null);

  useEffect(() => {
    if (!ticketId) {
      setError('No ticket ID provided');
      setLoading(false);
      return;
    }

    const fetchWorkflow = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(`v2/maintenance/workflow/${ticketId}/history`);
        if (res.data.success) {
          setWorkflow(res.data.data.workflow);
        } else {
          setError(res.data.message || 'Failed to fetch ticket history');
        }
      } catch (err) {
        console.error('Error fetching workflow:', err);
        setError(err.response?.data?.message || 'Failed to load ticket history');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflow();
  }, [ticketId]);

  const groupedTimeline = useMemo(() => {
    if (!workflow?.history?.length) return [];
    const grouped = [];
    let currentGroup = null;
    let recentStatus = null;

    const shouldGroupWithCurrent = (entry, group) => {
      if (!group) return false;
      const ca = group.entries[0].action;
      const na = entry.action;
      if (ca === 'status_changed' && recentStatus) {
        if (recentStatus === 'Scheduled' && na === 'scheduling_set') return true;
        if (recentStatus === 'Inprogress' && na === 'progress_updated') return true;
        if (recentStatus === 'Completed' && (na === 'closure_updated' || na === 'attachments_added')) return true;
      }
      if (ca === 'status_changed' && na === 'status_changed') {
        return entry.details.to === group.entries[0].details.to;
      }
      return false;
    };

    [...workflow.history].reverse().forEach(entry => {
      if (entry.action === 'status_changed') recentStatus = entry.details.to;
      if (shouldGroupWithCurrent(entry, currentGroup)) {
        currentGroup.entries.push(entry);
      } else {
        if (currentGroup) grouped.push(currentGroup);
        currentGroup = { entries: [entry] };
      }
    });

    if (currentGroup) grouped.push(currentGroup);
    return grouped;
  }, [workflow?.history]);

  const openImagePreview = async (url, title = 'Image Preview', mediaType = null) => {
    setLoading(true);
    let previewUrl = url;
    const needsSigning =
      !(
        url.startsWith('blob:') ||
        url.startsWith('data:') ||
        url.includes('?') ||
        url.includes('Expires=') ||
        url.includes('Signature=') ||
        url.includes('X-Amz-')
      );

    if (needsSigning) {
      try {
        const res = await axios.post('v2/inventory/consume/get-signed-urls', { urls: [url] });
        if (res.data.success && res.data.signed_urls?.length) {
          previewUrl = res.data.signed_urls[0].signed_url || url;
        }
      } catch (err) {
        console.error('Error getting signed URL:', err);
      }
    }

    setImagePreviewModal({ url: previewUrl, title, mediaType });
    setLoading(false);
  };

  const closeImagePreview = () => setImagePreviewModal(null);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="text-muted mt-3">Loading ticket history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert color="danger" className="m-3">
        <FeatherIcon icon="alert-circle" size={16} className="me-2" />
        {error}
      </Alert>
    );
  }

  if (!workflow) {
    return (
      <div className="text-center py-5 text-muted">
        <FeatherIcon icon="inbox" size={48} className="mb-3 opacity-50" />
        <p>No workflow data found</p>
      </div>
    );
  }

  return (
    <>
      <div className="timeline-compact px-4 py-3">
        {/* Closure Summary */}
        {workflow?.closure && workflow?.closure.closedAt && (
          <div className="closure-summary card border-0 bg-success-subtle mb-4 shadow-sm">
            <div className="card-body py-3 px-4">
              <div className="d-flex align-items-start gap-3">
                <div className="rounded-circle bg-success text-white p-2 d-flex">
                  <FeatherIcon icon="check-circle" size={20} />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-baseline">
                    <strong className="text-success">Ticket Completed</strong>
                    <small className="text-muted">
                      {new Date(workflow.closure.closedAt).toLocaleString([], {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </small>
                  </div>
                  <div className="small mt-1">
                    by {workflow.closure.closedBy?.first_name} {workflow.closure.closedBy?.last_name}
                  </div>
                  {workflow.closure.notes && (
                    <div className="mt-3">
                      <div className="small fw-semibold mb-1 text-success-emphasis">Completion Notes</div>
                      <div className="small bg-white p-2 rounded border">
                        {workflow.closure.notes}
                      </div>
                    </div>
                  )}
                  {workflow.closure.attachments?.length > 0 && (
                    <div className="mt-3">
                      <div className="small fw-semibold mb-2 d-flex align-items-center gap-2">
                        <FeatherIcon icon="image" size={14} />
                        Attachments ({workflow.closure.attachments.length})
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {workflow.closure.attachments.map((att, idx) => (
                          <div
                            key={att._id || idx}
                            className="position-relative rounded overflow-hidden shadow-sm"
                            style={{ width: '76px', height: '76px', cursor: 'pointer' }}
                            onClick={() => openImagePreview(att.url, `Attachment ${idx + 1}`, att.type)}
                          >
                            <MediaWithSignedUrl
                              mediaUrl={att.url}
                              mediaType={att.type}
                              className="w-100 h-100"
                              style={{ objectFit: 'cover' }}
                              isThumbnail={true}
                              onClick={() => openImagePreview(att.url, `Attachment ${idx + 1}`, att.type)}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Timeline Items */}
        <div className="timeline-items position-relative ps-4">
          <div className="timeline-line position-absolute start-0 top-0 bottom-0 w-px bg-secondary opacity-25" style={{ left: '11px' }} />

          {groupedTimeline.map((group, groupIdx) => {
            const first = group.entries[0];
            const allEntries = group.entries;
            const isStatusGroup = first.action === 'status_changed';
            const isSchedulingGroup = allEntries.some(e => e.action === 'scheduling_set');
            const isProgressGroup = allEntries.some(e => e.action === 'progress_updated');
            const isClosureGroup = allEntries.some(e => e.action === 'closure_updated') ||
              allEntries.some(e => e.action === 'attachments_added');

            let color = 'secondary', icon = 'activity', title = first.action.replace(/_/g, ' ');
            let statusCount = 0;

            if (isStatusGroup) {
              const status = first.details.to;
              statusCount = allEntries.filter(e => e.action === 'status_changed').length;
              color = status === 'Completed' ? 'success' :
                status === 'Inprogress' ? 'warning' :
                  status === 'Scheduled' ? 'primary' : 'info';
              icon = 'git-commit';
              if (isSchedulingGroup) title = `Scheduled Visit`;
              else if (isProgressGroup) title = `Work In Progress`;
              else if (isClosureGroup) title = `Work Completed`;
              else title = statusCount > 1 ? `Status → ${status} (${statusCount}×)` : `Status → ${status}`;
            } else if (first.action === 'scheduling_set') {
              color = 'primary'; icon = 'calendar'; title = 'Visit Scheduled';
            } else if (first.action === 'progress_updated') {
              color = 'warning'; icon = 'edit-3'; title = 'Progress Updated';
            } else if (first.action === 'closure_updated') {
              color = 'success'; icon = 'check-square'; title = 'Closure Updated';
            } else if (first.action === 'attachments_added') {
              color = 'info'; icon = 'image'; title = `Added ${first.details.count || '?'} attachment(s)`;
            }

            const latestEntry = allEntries[allEntries.length - 1];
            const timestamp = new Date(latestEntry.at).toLocaleString([], {
              dateStyle: 'medium',
              timeStyle: 'short'
            });

            return (
              <div key={groupIdx} className="timeline-group mb-3">
                <div className="d-flex position-relative">
                  <div
                    className={`timeline-dot rounded-circle d-flex align-items-center justify-content-center bg-${color} text-white flex-shrink-0`}
                    style={{ width: '26px', height: '26px', left: '-26px', top: '6px' }}
                  >
                    <FeatherIcon icon={icon} size={13} />
                  </div>
                  <div className="bg-white border rounded px-3 py-2 flex-grow-1 shadow-xs">
                    <div className="d-flex justify-content-between align-items-baseline small mb-1">
                      <strong className={`text-${color} mb-0`}>{title}</strong>
                      <span className="text-muted">{timestamp}</span>
                    </div>
                    <div className="text-muted smallest mb-2">
                      by {first.byName || 'System'}
                    </div>
                    <div className="timeline-group-content">
                      {isStatusGroup && first.details.from !== first.details.to && (
                        <div className="mb-2">
                          <span className="badge bg-light text-dark me-1">{first.details.from}</span>
                          <FeatherIcon icon="arrow-right" size={12} className="text-muted mx-1" />
                          <span className={`badge bg-${color}-subtle text-${color} ms-1`}>{first.details.to}</span>
                        </div>
                      )}

                      {/* Scheduling Details */}
                      {isSchedulingGroup && (
                        <div className="bg-light p-2 rounded mb-2">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fw-semibold text-primary small">
                              <FeatherIcon icon="calendar" size={12} className="me-1" />
                              Schedule Details
                            </span>
                          </div>
                          <Row className="g-2 small">
                            <Col xs={4}>
                              <div className="text-muted">
                                <span>Date:</span>
                                <span className="text-primary fw-medium ms-2">
                                  {new Date(allEntries.find(e => e.action === 'scheduling_set')?.details.date).toLocaleDateString()}
                                </span>
                              </div>
                            </Col>
                            <Col xs={4}>
                              <div className="text-muted">
                                <span>Time:</span>
                                <span className="text-primary fw-medium ms-2">
                                  {allEntries.find(e => e.action === 'scheduling_set')?.details.timeSlot.start} – {allEntries.find(e => e.action === 'scheduling_set')?.details.timeSlot.end}
                                </span>
                              </div>
                            </Col>
                            {allEntries.find(e => e.action === 'scheduling_set')?.details.technicianName && (
                              <Col xs={4}>
                                <div className="text-muted">
                                  <span>Technician:</span>
                                  <span className="text-primary fw-medium ms-2">
                                    {allEntries.find(e => e.action === 'scheduling_set')?.details.technicianName}
                                  </span>
                                </div>
                              </Col>
                            )}
                          </Row>
                        </div>
                      )}

                      {/* Progress Details */}
                      {isProgressGroup && (
                        <div className="bg-light p-2 rounded mb-2">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fw-semibold text-warning small">
                              <FeatherIcon icon="tool" size={12} className="me-1" />
                              Progress Update
                            </span>
                          </div>
                          <div className="progress-details">
                            {allEntries.find(e => e.action === 'progress_updated')?.details.new?.notes && (
                              <div className="mb-2 small">
                                <div className="fw-semibold mb-1">Notes:</div>
                                <div className="text-muted bg-white p-1 rounded">
                                  {allEntries.find(e => e.action === 'progress_updated')?.details.new.notes.length > 100
                                    ? allEntries.find(e => e.action === 'progress_updated')?.details.new.notes.substring(0, 98) + '…'
                                    : allEntries.find(e => e.action === 'progress_updated')?.details.new.notes}
                                </div>
                              </div>
                            )}
                            <Row className="g-2 text-muted small">
                              {allEntries.find(e => e.action === 'progress_updated')?.details.new?.partsOrdered && (
                                <Col xs={12} md={4}>
                                  <div>
                                    <span>Parts:</span>
                                    <span className="text-warning fw-medium ms-2">
                                      {allEntries.find(e => e.action === 'progress_updated')?.details.new.partsOrdered}
                                    </span>
                                  </div>
                                </Col>
                              )}
                              {allEntries.find(e => e.action === 'progress_updated')?.details.new?.expectedArrivalDate && (
                                <Col xs={6} md={4}>
                                  <div>
                                    <span>Arrival:</span>
                                    <span className="text-warning fw-medium ms-2">
                                      {new Date(allEntries.find(e => e.action === 'progress_updated')?.details.new.expectedArrivalDate).toLocaleDateString()}
                                    </span>
                                  </div>
                                </Col>
                              )}
                              {allEntries.find(e => e.action === 'progress_updated')?.details.new?.followUpDate && (
                                <Col xs={6} md={4}>
                                  <div>
                                    <span>Follow-up:</span>
                                    <span className="text-warning fw-medium ms-2">
                                      {new Date(allEntries.find(e => e.action === 'progress_updated')?.details.new.followUpDate).toLocaleDateString()}
                                    </span>
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </div>
                        </div>
                      )}

                      {/* Closure Details */}
                      {isClosureGroup && (
                        <div className="bg-light p-2 rounded mb-2">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fw-semibold text-success small">
                              <FeatherIcon icon="check-square" size={12} className="me-1" />
                              Completion Details
                            </span>
                          </div>
                          {allEntries.find(e => e.action === 'closure_updated')?.details.new?.notes && (
                            <div className="mb-2 small">
                              <div className="fw-semibold mb-1">Notes:</div>
                              <div className="text-success bg-white p-1 rounded border-start border-3 border-success">
                                {allEntries.find(e => e.action === 'closure_updated')?.details.new.notes.length > 120
                                  ? allEntries.find(e => e.action === 'closure_updated')?.details.new.notes.substring(0, 118) + '…'
                                  : allEntries.find(e => e.action === 'closure_updated')?.details.new.notes}
                              </div>
                            </div>
                          )}
                          {allEntries.some(e => e.action === 'attachments_added') && (
                            <div className="mt-2">
                              <div className="small fw-semibold mb-2 text-success">
                                <FeatherIcon icon="image" size={12} className="me-1" />
                                Attachments Added
                              </div>
                              <div className="d-flex flex-wrap gap-1">
                                {allEntries
                                  .filter(e => e.action === 'attachments_added')
                                  .flatMap(e => e.details.files || [])
                                  .slice(0, 6)
                                  .map((f, i) => (
                                    <div
                                      key={i}
                                      className="rounded overflow-hidden shadow-xs cursor-pointer"
                                      style={{ width: '48px', height: '48px' }}
                                      onClick={() => openImagePreview(f.url, `Attachment ${i + 1}`, f.type)}
                                    >
                                      <MediaWithSignedUrl
                                        mediaUrl={f.url}
                                        className="w-100 h-100 object-fit-cover"
                                        isThumbnail={true}
                                      />
                                    </div>
                                  ))}
                                {allEntries.filter(e => e.action === 'attachments_added').flatMap(e => e.details.files || []).length > 6 && (
                                  <div className="d-flex align-items-center justify-content-center bg-light border rounded text-muted" style={{ width: '48px', height: '48px' }}>
                                    <small>+{allEntries.filter(e => e.action === 'attachments_added').flatMap(e => e.details.files || []).length - 6}</small>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {(!workflow?.history || workflow.history.length === 0) && (
            <div className="text-center py-5 text-muted">
              <FeatherIcon icon="clock" size={40} className="mb-3 opacity-50" />
              <p className="mb-1">No activity recorded yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      <Modal
        isOpen={!!imagePreviewModal}
        toggle={closeImagePreview}
        size="xl"
        centered
        zIndex={2000}
        className="image-preview-modal"
      >
        <ModalHeader toggle={closeImagePreview}>
          {imagePreviewModal?.title || 'Media Preview'}
        </ModalHeader>
        <ModalBody className="text-center p-4">
          <div style={{ maxHeight: '75vh', overflow: 'auto' }}>
            {imagePreviewModal && (
              <MediaWithSignedUrl
                mediaUrl={imagePreviewModal.url}
                mediaType={imagePreviewModal.mediaType}
                style={{
                  maxHeight: '75vh',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                }}
              />
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              const link = document.createElement('a');
              link.href = imagePreviewModal?.url;
              link.download = `${(imagePreviewModal?.title || 'preview').replace(/[^a-z0-9]/gi, '_')}.jpg`;
              link.click();
            }}
          >
            <FeatherIcon icon="download" className="me-2" />
            Download
          </Button>
          <Button color="secondary" onClick={closeImagePreview}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};